import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './css/FeedbackManagement.css';
import { formatDateTime } from './utils/Common';

const FeedbackManagement = () => {
  const { hasPermission } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'new';

  const canManageFeedback = hasPermission('feedback:manage');

  useEffect(() => {
    if (canManageFeedback) {
      fetchFeedback();
    }
  }, [canManageFeedback]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/feedback');
      setFeedback(response.data.feedback || []);
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setUpdating(prev => ({ ...prev, [id]: true }));
      await api.patch(`/feedback/${id}`, { status: newStatus });
      setFeedback(prev =>
        prev.map(f => (f.id === id ? { ...f, status: newStatus } : f))
      );
    } catch (err) {
      console.error('Error updating feedback status:', err);
      setError('Failed to update status');
    } finally {
      setUpdating(prev => ({ ...prev, [id]: false }));
    }
  };

  const getSubjectLabel = (subject) => {
    const labels = {
      suggestion: 'Sugestia ulepszenia',
      bug: 'Zgłoszenie błędy',
      automation: 'Pomysł na automatyzację',
      
    };
    return labels[subject] || subject;
  };

  const getSubjectIcon = (subject) => {
    const icons = {
      suggestion: '💡',
      bug: '🐛',
      automation: '🤖',
    };
    return icons[subject] || '📝';
  };

  // Filter feedback by status
  const newFeedback = feedback.filter(f => f.status === 'new');
  const acceptedFeedback = feedback.filter(f => f.status === 'accepted');
  const rejectedFeedback = feedback.filter(f => f.status === 'rejected');

  const currentFeedback = activeTab === 'new' ? newFeedback 
    : activeTab === 'accepted' ? acceptedFeedback 
    : rejectedFeedback;

  if (!canManageFeedback) {
    return (
      <div className="feedback-management-page">
        <div className="page-header">
          <h2>🚫 Access Denied</h2>
          <p>You don't have permission to manage feedback.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-management-page">
      <div className="page-header">
        <h2>💬 Feedback Management</h2>
        <p>Review and manage user feedback submissions</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab-btn ${activeTab === 'new' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'new'); return next; })}
        >
          📬 New ({newFeedback.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'accepted' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'accepted'); return next; })}
        >
          ✅ Accepted ({acceptedFeedback.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'rejected'); return next; })}
        >
          ❌ Rejected ({rejectedFeedback.length})
        </button>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="loading-spinner">Loading feedback...</div>
        ) : currentFeedback.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              {activeTab === 'new' ? '📭' : activeTab === 'accepted' ? '✅' : '❌'}
            </div>
            <h3>No {activeTab} feedback</h3>
            <p>There are no {activeTab} feedback submissions at this time.</p>
          </div>
        ) : (
          <div className="feedback-list">
            {currentFeedback.map((item) => (
              <div key={item.id} className={`feedback-card ${item.status}`}>
                <div className="feedback-header">
                  <div className="feedback-meta">
                    <span className="feedback-icon">{getSubjectIcon(item.subject)}</span>
                    <span className="feedback-subject">{getSubjectLabel(item.subject)}</span>
                    <span className="feedback-id">#{item.id}</span>
                  </div>
                  <div className="feedback-date">{formatDateTime(item.created_at)}</div>
                </div>

                <div className="feedback-body">
                  <p className="feedback-message">{item.message}</p>
                </div>

                <div className="feedback-footer">
                  <div className="feedback-user">
                    <span className="user-icon">👤</span>
                    <span>{item.user_name}</span>
                  </div>

                  <div className="feedback-actions">
                    {activeTab === 'new' && (
                      <>
                        <button
                          className="action-btn accept"
                          onClick={() => handleStatusChange(item.id, 'accepted')}
                          disabled={updating[item.id]}
                        >
                          {updating[item.id] ? '...' : '✓ Accept'}
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => handleStatusChange(item.id, 'rejected')}
                          disabled={updating[item.id]}
                        >
                          {updating[item.id] ? '...' : '✕ Reject'}
                        </button>
                      </>
                    )}
                    {activeTab === 'accepted' && (
                      <button
                        className="action-btn revert"
                        onClick={() => handleStatusChange(item.id, 'new')}
                        disabled={updating[item.id]}
                      >
                        {updating[item.id] ? '...' : '↩ Move to New'}
                      </button>
                    )}
                    {activeTab === 'rejected' && (
                      <button
                        className="action-btn revert"
                        onClick={() => handleStatusChange(item.id, 'new')}
                        disabled={updating[item.id]}
                      >
                        {updating[item.id] ? '...' : '↩ Move to New'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackManagement;
